/**
 * Legal-document metadata shared between the web app's legal pages and the
 * backend's legal-update broadcast email. This module holds NO JSX (the
 * rendered prose lives in `apps/web/.../legal-content.tsx`), so the
 * backend can import the `lastUpdated` dates it gates the broadcast on.
 *
 * The `lastUpdated` string is the version gate: bump it here when a document's
 * content materially changes, and the next production boot emails every user
 * whose recorded stamp is older (see `LegalUpdateBroadcastService`). Editing
 * the date in one place moves both the public page and the email gate.
 *
 * Stays in `@naumu/shared` (mirrors `data/release-notes`) because both web and
 * backend consume it.
 */

export type LegalDocumentType = 'terms-of-service' | 'privacy-policy' | 'cookie-policy';

export interface LegalDocMeta {
	type: LegalDocumentType;
	title: string;
	/** Human date string, e.g. `"June 2, 2026"`. Parsed via `Date.parse`. */
	lastUpdated: string;
}

/** Stable ordering for the CAS key and the per-doc diff. */
export const LEGAL_DOCUMENT_TYPES: readonly LegalDocumentType[] = [
	'terms-of-service',
	'privacy-policy',
	'cookie-policy',
];

export const LEGAL_DOCUMENT_META: Record<LegalDocumentType, LegalDocMeta> = {
	'terms-of-service': {
		type: 'terms-of-service',
		title: 'Terms of Service',
		lastUpdated: 'June 2, 2026',
	},
	'privacy-policy': {
		type: 'privacy-policy',
		title: 'Privacy Policy',
		lastUpdated: 'June 2, 2026',
	},
	'cookie-policy': {
		type: 'cookie-policy',
		title: 'Cookie Policy',
		lastUpdated: 'April 30, 2026',
	},
};

/**
 * A snapshot of every doc's current `lastUpdated`. This is the unit stored
 * per-user (as a JSON string) and compared on each broadcast run.
 */
export type LegalVersionMap = Partial<Record<LegalDocumentType, string>>;

/** The current `lastUpdated` of every legal document, as a complete map. */
export const currentLegalVersions = (): Record<LegalDocumentType, string> => ({
	'terms-of-service': LEGAL_DOCUMENT_META['terms-of-service'].lastUpdated,
	'privacy-policy': LEGAL_DOCUMENT_META['privacy-policy'].lastUpdated,
	'cookie-policy': LEGAL_DOCUMENT_META['cookie-policy'].lastUpdated,
});

/**
 * Deterministic, order-independent key over all doc dates — the
 * `LegalBroadcastMarker` CAS value. Any `lastUpdated` change flips the key,
 * which is what makes one (and only one) pod dispatch per change.
 */
export const legalVersionKey = (
	versions: LegalVersionMap = currentLegalVersions(),
): string => LEGAL_DOCUMENT_TYPES.map((t) => `${t}=${versions[t] ?? ''}`).join('|');

/**
 * The doc types whose `current` date is strictly NEWER than the user's
 * `stored` date (or absent from `stored`). Newer-than — not merely different —
 * so a rollback to an older date never re-emails. An unparseable `current`
 * date is skipped (never emailed); an unparseable `stored` date is treated as
 * "needs notifying". Returns `[]` when nothing is newer.
 *
 * First-touch (the whole `stored` map null/empty) is the caller's concern:
 * the broadcast service seeds the stamp and sends nothing, so it never reaches
 * here with a null map expecting an email.
 */
export const diffLegalVersions = (
	stored: LegalVersionMap | null | undefined,
	current: Record<LegalDocumentType, string> = currentLegalVersions(),
): LegalDocumentType[] => {
	const changed: LegalDocumentType[] = [];
	for (const type of LEGAL_DOCUMENT_TYPES) {
		const currentMs = Date.parse(current[type]);
		if (Number.isNaN(currentMs)) continue;
		const storedStr = stored?.[type];
		if (storedStr == null) {
			changed.push(type);
			continue;
		}
		const storedMs = Date.parse(storedStr);
		if (Number.isNaN(storedMs) || currentMs > storedMs) changed.push(type);
	}
	return changed;
};
